import { escapeAttributeValue, escapeHtml, isDocumentFragment, isElement, sanitizeAttributeName, typeCheck, } from "@knyt/artisan";
import { build } from "../build/mod.js";
import { KEY_ATTRIBUTE } from "../constants.js";
import { renderCSS } from "../CSSRenderer.js";
import { shouldRecognizeChildren } from "../shouldRecognizeChildren.js";
import { syncCustomElementRegistries } from "../syncCustomElementRegistries.js";
import { isUpdatableHost, uponElementUpdatesSettled, } from "../uponElementUpdatesSettled.js";
import { containsOnlyStrings, createFragmentDeclaration, createMarkupDeclaration, createStringDocumentFragment, createUnescapedString, extractTagNameFromElementBuilder, getElementDeclarationFromElementBuilder, getElementDeclarationFromViewBuilder, isElementBuilder, isElementDeclaration, isStringDocumentFragment, isUnescapedString, isViewBuilder, nodesToElementBuilderChildren, nodeToHtml, renderStringsOnlyFragment, } from "../utils/mod.js";
/**
 * The default value for the `disableKeyAttributes` option
 * for the `render` function. This should be the fallback
 * value when passing options to the `build` function.
 *
 * @defaultValue false
 */
const DEFAULT_DISABLE_KEY_ATTRIBUTES = false;
const DEFAULT_ENABLE_WHITESPACE = false;
/**
 * The default value for the `reactiveElementTimeout` option.
 */
const DEFAULT_REACTIVE_ELEMENT_TIMEOUT = 0;
/**
 * Void elements are elements that cannot have children.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Glossary/Void_element | MDN Reference}
 */
var VoidElement;
(function (VoidElement) {
    VoidElement["Area"] = "area";
    VoidElement["Base"] = "base";
    VoidElement["Br"] = "br";
    VoidElement["Col"] = "col";
    VoidElement["Embed"] = "embed";
    VoidElement["Hr"] = "hr";
    VoidElement["Img"] = "img";
    VoidElement["Input"] = "input";
    VoidElement["Link"] = "link";
    VoidElement["Meta"] = "meta";
    VoidElement["Source"] = "source";
    VoidElement["Track"] = "track";
    VoidElement["Wbr"] = "wbr";
})(VoidElement || (VoidElement = {}));
const VOID_ELEMENTS = Object.values(VoidElement);
async function renderChildren(children, options) {
    const { enableWhitespace = false } = options;
    const whitespace = enableWhitespace ? "\n" : "";
    return (await Promise.all(children.map((child) => renderChildToString(child, options)))).join(whitespace);
}
async function renderChildToString(child, options) {
    if (typeof child === "string" || typeof child === "number") {
        return escapeHtml(String(child));
    }
    if (isElement(child)) {
        return renderElement(child, options);
    }
    if (isDocumentFragment(child)) {
        return renderDocumentFragment(child, options);
    }
    if (isStringDocumentFragment(child)) {
        return renderStringDocumentFragment(child, options);
    }
    return render(child, options);
}
async function renderDocumentFragment(fragment, options) {
    return childNodesToString(fragment.childNodes, options);
}
function isElementWithRenderToString(element) {
    return (typeof element.renderToString === "function");
}
async function domElementBuilderToString(elementBuilder, options) {
    const $document = options.document ?? globalThis.document;
    const nextOptions = { ...options, document: $document };
    const tagName = extractTagNameFromElementBuilder(elementBuilder);
    if (!options.disableShadowDOMRendering) {
        syncCustomElementRegistries(tagName, globalThis.customElements, $document.defaultView?.customElements);
    }
    const sharedOptions = {
        document: $document,
        logger: options.logger,
        disableKeyAttributes: options.disableKeyAttributes ?? DEFAULT_DISABLE_KEY_ATTRIBUTES,
    };
    // TODO: Considering pulling out children and rendering them separately
    // from the containing element. This could avoid unnecessary conversions
    // from strings to elements and back to strings.
    const el = await build(elementBuilder, sharedOptions);
    // If for some reason the builder returns `null`, we return
    // an empty string. This shouldn't happen, but it's a good
    // safety check.
    if (el === null)
        return "";
    if (isElement(el)) {
        return renderElement(el, nextOptions);
    }
    return nodeToHtml($document, el);
}
async function renderElement(el, options) {
    const $document = options.document ?? globalThis.document;
    const nextOptions = { ...options, document: $document };
    const reactiveElementTimeout = options.reactiveElementTimeout !== undefined
        ? options.reactiveElementTimeout
        : DEFAULT_REACTIVE_ELEMENT_TIMEOUT;
    if (reactiveElementTimeout !== false && isUpdatableHost(el)) {
        await uponElementUpdatesSettled(el, reactiveElementTimeout);
    }
    // Each child is rendered to a string, with each being processed by `render()`
    const childNodesString = await childNodesToString(el.childNodes, nextOptions);
    // If the element has a `renderToString` method, we call it
    // to render the element's shadow DOM.
    if (!options.disableRenderToString && isElementWithRenderToString(el)) {
        return el.renderToString(childNodesString, nextOptions);
    }
    // Otherwise, we clone the element as a declaration without its children,
    // and set the `childNodesString` as the inner HTML of the declaration.
    //
    // The strategy here is to avoids:
    // 1. Converting the children from HTML to DOM and back to HTML.
    // 2. Gives us the opportunity to render a declarative shadow DOM,
    // by not converting the HTML into DOM.
    // ---
    // NOTE: Only the element's attributes are relevant here.
    // Properties, event listeners, and similar runtime data are not
    // represented in HTML, so they do not need to be serialized at this stage.
    //
    // If an element requires certain properties to be serialized,
    // it should implement its own serialization and rehydration logic.
    //
    // For `KnytElement` instances, use `hydrateProp` or `hydrateRef`
    // to handle serialization and rehydration of properties without
    // exposing them as attributes.
    //
    // This approach mirrors the behavior of the `cloneNode` method.
    // `cloneNode` copies only the element's attributes and children,
    // excluding properties, event listeners, and other runtime data.
    // Its purpose is to duplicate the element's structure,
    // not its runtime state.
    const attributes = {};
    for (const name of el.getAttributeNames()) {
        // NOTE: We won't try to interpret the attribute value here;
        // we will simply copy it as a string.
        //
        // We don't want to make assumptions about the attribute value.
        // For example, boolean attributes may be interpreted as both an
        // empty string value or `true`.
        //
        // See: https://developer.mozilla.org/en-US/docs/Glossary/Boolean/HTML
        attributes[name] = el.getAttribute(name);
    }
    const openShadowRoot = el.shadowRoot && !options.disableShadowDOMRendering
        ? await renderOpenShadowRoot(el.shadowRoot, nextOptions)
        : null;
    const tagName = el.tagName.toLowerCase();
    const cloneDeclaration = createMarkupDeclaration(tagName, attributes, [
        openShadowRoot,
        createStringDocumentFragment.fromString(childNodesString),
    ]);
    return render(cloneDeclaration, nextOptions);
}
// Locally reference the `JSON.stringify` function
// to avoid some potential issues with global overrides.
const jsonStringify = JSON.stringify;
function renderAttribute(name, value) {
    const sanitizedName = sanitizeAttributeName(name);
    if (value == null) {
        return undefined;
    }
    if (typeof value === "boolean") {
        return value ? sanitizedName : undefined;
    }
    if (typeof value === "string" || typeof value === "number") {
        return `${sanitizedName}="${escapeAttributeValue(String(value))}"`;
    }
    if (typeof value === "object") {
        return `${sanitizedName}="${escapeAttributeValue(jsonStringify(value))}"`;
    }
    typeCheck(typeCheck.identify(value));
    throw new TypeError(`Invalid attribute value type: ${typeof value}. Expected string, boolean, or object.`);
}
/**
 * @internal scope: package
 */
export function renderAttributes(attributes) {
    const renderedAttributes = [];
    for (const [name, value] of Object.entries(attributes)) {
        const render = renderAttribute(name, value);
        if (render) {
            renderedAttributes.push(render);
        }
    }
    return renderedAttributes;
}
/**
 * Renders the given declaration into an HTML string
 *
 * @example
 *
 * ```ts
 * import { render, html } from "knyt";
 *
 * const htmlString = await render(html.div);
 * ```
 */
export async function render(input, options = {}) {
    const $document = options.document ?? globalThis.document;
    const { disableKeyAttributes = DEFAULT_DISABLE_KEY_ATTRIBUTES, enableWhitespace = DEFAULT_ENABLE_WHITESPACE, } = options;
    let declaration;
    if (input == null || input === false) {
        return "";
    }
    if (typeof input === "string") {
        return escapeHtml(input);
    }
    if (isElementBuilder(input)) {
        if (isElementBuilder.Fragment(input) || isElementBuilder.Markup(input)) {
            declaration = getElementDeclarationFromElementBuilder(input);
        }
        else if (isElementBuilder.DOM(input)) {
            return domElementBuilderToString(input, options);
        }
        else {
            throw new Error("Unknown element kind");
        }
    }
    else if (isViewBuilder(input)) {
        declaration = await getElementDeclarationFromViewBuilder(input);
    }
    else if (isElementDeclaration(input)) {
        declaration = input;
    }
    else if (isElement(input)) {
        // To avoid more complexity, we will simply convert the element into a declaration.
        declaration = createFragmentDeclaration([input]);
    }
    else {
        throw new TypeError(`Invalid input type: ${typeof input}. Input must be of type \`KnytDeclaration\`.`);
    }
    if (isElementDeclaration.Fragment(declaration)) {
        // To clarify, we don't need to check with `shouldRecognizeChildren` here,
        // because the `children` property is always recognized in a fragment.
        // This is because fragment's can't have a render mode.
        return renderChildren(declaration.children, options);
    }
    const { type, props, children, key, attributes } = 
    // Declarations for DOM elements and Fragments are handled above,
    // so this declaration must be an Markup declaration for a singular element.
    declaration;
    if (typeof type === "string") {
        const tagName = type;
        const { style, ...attributesFromProps } = props;
        const allAttributes = {
            ...attributesFromProps,
            // NOTE:`attributes` takes precedence over `props`,
            // because there are only special cases where `attributes` is
            // set in a markup element builder.
            // Currently, the only case where it's used in for htmx attributes.
            ...attributes,
        };
        const innerHtml = shouldRecognizeChildren(declaration, undefined)
            ? await renderChildren(children, options)
            : "";
        const renderedAttributes = renderAttributes(allAttributes);
        if (style) {
            const css = typeof style === "string" ? style : renderCSS($document, style);
            renderedAttributes.push(renderAttribute("style", css));
        }
        if (key && !disableKeyAttributes) {
            renderedAttributes.push(renderAttribute(KEY_ATTRIBUTE, key));
        }
        const whitespace = enableWhitespace ? "\n" : "";
        const attrsStr = renderedAttributes.length
            ? `${whitespace} ${renderedAttributes.join(`${whitespace} `)}${whitespace}`
            : "";
        if (innerHtml === "" && VOID_ELEMENTS.includes(tagName)) {
            return `<${tagName}${attrsStr}>`;
        }
        return `<${tagName}${attrsStr}>${whitespace}${innerHtml}${whitespace}</${tagName}>`;
    }
    return "";
}
(function (render) {
    /**
     * Calls the `render` function and prefixes the result with `<!doctype html>`.
     *
     * @see render
     * @public
     */
    async function withDocType(...args) {
        return `<!doctype html>${await render(...args)}`;
    }
    render.withDocType = withDocType;
})(render || (render = {}));
async function renderStringDocumentFragment(input, options) {
    if (containsOnlyStrings(input)) {
        return renderStringsOnlyFragment(input);
    }
    return renderInterpolatedStringDocumentFragment(input, options);
}
async function renderInterpolatedStringDocumentFragment(input, options) {
    const { markup, values } = input;
    const renderedValues = await Promise.all(values.map(async (value) => {
        if (isUnescapedString(value)) {
            return value;
        }
        return createUnescapedString(await render(createFragmentDeclaration([value]), options));
    }));
    return renderStringsOnlyFragment(createStringDocumentFragment(markup, renderedValues));
}
function childNodesToString(childNodes, options) {
    return render(createFragmentDeclaration(nodesToElementBuilderChildren(childNodes)), options);
}
async function renderOpenShadowRoot(shadowRoot, options) {
    const childNodesString = await childNodesToString(shadowRoot.childNodes, options);
    const childNodesFragment = createStringDocumentFragment.fromString(childNodesString);
    const templateDeclaration = createMarkupDeclaration("template", { shadowrootmode: "open" }, [childNodesFragment]);
    const templateString = await render(templateDeclaration, options);
    return createStringDocumentFragment.fromString(templateString);
}
